import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Dialog, DialogActions, DialogTitle, DialogContent, } from '@material-ui/core';
import { CancelOutlined, SendOutlined } from "@material-ui/icons";
import { toast } from "react-toastify";
import ReactImgEditor from "@cloudogu/react-img-editor";
import "@cloudogu/react-img-editor/lib/index.css";

import { i18n } from "../../translate/i18n";
import { getPixelRatioFromCanvas } from "../../utils/getPixelRatioFromCanvas";
import api from "../../services/api";
import MediaNotSupportedModal from "../MediaNotSupportedModal";
import "../../styles/react-img-editor-new-styles.css";

const useStyles = makeStyles(theme => ({
  dialogContent: {
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },

  dialogActions: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
    margin: "1em",
  },

  floatingButton: {
    transition: "all 0.3s ease",
    "&:hover": { transform: "translateY(-5px)" },
  },
}));

const ImageToStickerContextModal = ({ open, onClose, imageToSticker, setImageToSticker, ticketId }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const stageRef = useRef(null);
  const imageUrl = imageToSticker ? URL.createObjectURL(imageToSticker) : "";

  const [isMediaNoSupportedModalOpen, setIsMediaNoSupportedModalOpen] = useState(false);
  const [mediasNotSupported, setMediasNotSupported] = useState([]);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    return () => URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => { 
    setImageToSticker(null);
    handleCloseMedialNotSupportedModal();
    onClose();
    document.getElementById("upload-image-to-sticker").value = "";
  };

  const handleOpenMedialNotSupportedModal = (medias) => {
    setIsMediaNoSupportedModalOpen(true);
    setMediasNotSupported(medias);
  };

  const handleCloseMedialNotSupportedModal = () => {
    setMediasNotSupported([]);
    setIsMediaNoSupportedModalOpen(false);
  };

  const setComponentStage = (stage) => {
    stageRef.current = stage;
  };

  const handleSendEditedImage = () => {
    const canvas = stageRef.current.clearAndToCanvas({ pixelRatio: getPixelRatioFromCanvas(stageRef) });

    canvas.toBlob(async blob => {
      try {
        const formData = new FormData();
        formData.append("fromMe", true);
        formData.append("media", blob, "image.png");
        handleClose();

        const { data } = await api.post(`/stickers/${ticketId}`, formData);
        if (data.invalidMedias?.length > 0) handleOpenMedialNotSupportedModal(data.invalidMedias);
      }
      catch (exception) {
        toast.error(i18n.t("stickersModal.toasts.sendSticker"));
      }
    });
  };

 

  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <MediaNotSupportedModal
        open={isMediaNoSupportedModalOpen}
        onClose={handleCloseMedialNotSupportedModal}
        medias={mediasNotSupported}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
        <DialogTitle>
          <span>{i18n.t("imageToStickerModal.title.up")}</span>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          <ReactImgEditor 
            language="en"
            src={imageUrl}
            crossOrigin="anonymous"
            getStage={setComponentStage}
            toolbar={{ items: ["pen", "eraser", "arrow", "rect", "circle", "mosaic", "text", "|", "repeal", "download", "crop"] }}
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.floatingButton}
            startIcon={<CancelOutlined />}
            color="inherit"
            variant="outlined"
            onClick={handleClose}
          >
            {i18n.t("imageToStickerModal.buttons.cancel")}
          </Button>

          <Button
            className={classes.floatingButton}
            startIcon={<SendOutlined />}
            color="inherit"
            variant="outlined"
            onClick={handleSendEditedImage}
          >
            {i18n.t("imageToStickerModal.buttons.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ImageToStickerContextModal;
